import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { ModuleStudioShell } from "../../module/ModuleStudioShell";
import { GymCountdownOverlay, type GymCountdownPhase } from "../../gym/GymCountdownOverlay";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { Button } from "../../ui/button";
import { CHORD_HUB_THEME as T } from "../../../lib/chords/chordHubTheme";
import type { GymDrillHubCard } from "../../../lib/chords/chordHubTypes";
import { getClassicVoicings, getChordDef } from "../../../lib/chordEngine";
import { getUkuleleClassicVoicings } from "../../../lib/ukuleleChordEngine";
import { useInstrument } from "../../../contexts/InstrumentContext";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

type StudioPhase = "intro" | "countdown" | "browse" | "complete";

export interface ChordGymDrillStudioProps {
  card: GymDrillHubCard;
  wasCompletedBefore: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ChordGymDrillStudio({
  card,
  wasCompletedBefore,
  onBack,
  onComplete,
}: ChordGymDrillStudioProps) {
  const { instrument, surface } = useInstrument();
  const isUkulele = instrument === "ukulele";

  const [phase, setPhase] = useState<StudioPhase>("intro");
  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [drillIndex, setDrillIndex] = useState(0);
  const [seenDrills, setSeenDrills] = useState<Set<number>>(() => new Set([0]));

  const drillSteps = useMemo(() => {
    return card.payload.drills.map((drill) => {
      const voicings = isUkulele
        ? getUkuleleClassicVoicings(drill.root, drill.quality as "major")
        : getClassicVoicings(drill.root, drill.quality);
      return { ...drill, voicing: voicings[0] ?? null };
    });
  }, [card.payload.drills, isUkulele]);

  const currentStep = drillSteps[drillIndex] ?? null;
  const def = currentStep ? getChordDef(currentStep.quality) : null;

  const mutedStrings = useMemo(() => currentStep?.voicing?.mutedStrings ?? [], [currentStep]);

  const highlightedPositions = useMemo(() => {
    if (!currentStep?.voicing) return [];
    return currentStep.voicing.positions.filter((p) => !mutedStrings.includes(p.string));
  }, [currentStep, mutedStrings]);

  const allSeen = drillSteps.length > 0 && seenDrills.size >= drillSteps.length;

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        setPhase("browse");
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const goToDrill = useCallback(
    (next: number) => {
      if (drillSteps.length === 0) return;
      const idx = ((next % drillSteps.length) + drillSteps.length) % drillSteps.length;
      setDrillIndex(idx);
      setSeenDrills((prev) => new Set(prev).add(idx));
    },
    [drillSteps.length]
  );

  const handleFinish = useCallback(() => {
    setPhase("complete");
    window.setTimeout(() => {
      onComplete();
    }, 900);
  }, [onComplete]);

  const dimNeck = phase === "intro" || countdown !== null;
  const promptText =
    drillSteps.length > 0 && currentStep
      ? `${currentStep.label ?? `${currentStep.root} ${def?.label ?? currentStep.quality}`} (${drillIndex + 1}/${drillSteps.length})`
      : "Sin acordes disponibles";

  if (surface === "keyboard") {
    return (
      <ModuleStudioShell
        title={card.title}
        subtitle={card.subtitle}
        onBack={onBack}
        accent={T.accent}
        border={T.border}
        textMain={T.textMain}
        textMuted={T.textMuted}
        textDim={T.textDim}
      >
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <p className="text-sm max-w-md" style={{ color: T.textMuted }}>
            Este drill está optimizado para guitarra y ukelele. Cambia el instrumento de sesión.
          </p>
          <Button onClick={onBack} className="mt-4" variant="outline">
            Volver al hub
          </Button>
        </div>
      </ModuleStudioShell>
    );
  }

  return (
    <ModuleStudioShell
      title={card.title}
      subtitle={card.subtitle}
      onBack={onBack}
      accent={T.accent}
      border={T.border}
      textMain={T.textMain}
      textMuted={T.textMuted}
      textDim={T.textDim}
      headerExtra={
        phase === "browse" && drillSteps.length > 0 ? (
          <span className="text-[10px] tabular-nums" style={{ color: T.textDim }}>
            {seenDrills.size}/{drillSteps.length} acordes
          </span>
        ) : undefined
      }
    >
      <div className="flex-1 min-h-[min(62vh,560px)] flex items-center justify-center p-2 sm:p-4 relative rounded-2xl overflow-hidden">
        <div
          className="relative w-full h-full max-w-[min(96vw,1100px)] mx-auto flex items-center justify-center transition-opacity duration-500 ease-out"
          style={{ opacity: dimNeck ? 0.35 : 1 }}
        >
          {currentStep?.voicing ? (
            <PracticeSurface
              className="h-full w-full"
              highlightedPositions={highlightedPositions}
              mutedStrings={mutedStrings}
              barre={currentStep.voicing.barre ?? null}
              showNoteNames
              showDegrees
              visualStyle="lutherie"
            />
          ) : (
            <p className="text-sm" style={{ color: T.textMuted }}>
              No hay forma clásica para este acorde en {isUkulele ? "ukelele" : "guitarra"}.
            </p>
          )}
        </div>

        {phase === "intro" && (
          <ModuleIntroModal
            title={card.title}
            subtitle={card.subtitle}
            description={card.description}
            instructions={card.instructions}
            tags={card.tags}
            timeEstimateMin={card.timeEstimateMin}
            accent={T.accent}
            accentSoft={T.accentSoft}
            accentGlow={T.accentGlow}
            borderHover={T.borderHover}
            textMain={T.textMain}
            textMuted={T.textMuted}
            textDim={T.textDim}
            wasCompletedBefore={wasCompletedBefore}
            onBegin={handleBeginCountdown}
          />
        )}

        {countdown !== null && <GymCountdownOverlay phase={countdown} />}

        {phase === "browse" && (
          <>
            <div className="absolute top-3 left-1/2 z-30 w-[min(92%,420px)] -translate-x-1/2 pointer-events-none">
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-2xl"
                style={{
                  background: "rgba(28,26,25,0.9)",
                  border: `1px solid ${T.borderHover}`,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                <span
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: T.accentSoft, color: T.accent }}
                >
                  {drillIndex + 1}
                </span>
                <p className="text-sm font-medium flex-1" style={{ color: T.textMain }}>
                  {promptText}
                  {def && (
                    <span className="block text-[11px] font-normal mt-0.5" style={{ color: T.textDim }}>
                      {def.degreeNames.join(" · ")}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="absolute bottom-4 left-1/2 z-30 -translate-x-1/2 flex flex-wrap items-center justify-center gap-2 pointer-events-auto px-2">
              <button
                type="button"
                onClick={() => goToDrill(drillIndex - 1)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/10"
                style={{ border: `1px solid ${T.border}`, color: T.textMuted }}
                aria-label="Acorde anterior"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <Button
                onClick={handleFinish}
                disabled={!allSeen}
                className="font-semibold text-white border-0 px-5"
                style={{
                  backgroundColor: allSeen ? T.accent : "rgba(255,255,255,0.12)",
                  opacity: allSeen ? 1 : 0.7,
                }}
              >
                {allSeen ? "Completar drill" : `Ver ${drillSteps.length - seenDrills.size} más`}
              </Button>
              <button
                type="button"
                onClick={() => goToDrill(drillIndex + 1)}
                className="w-10 h-10 rounded-xl flex items-center justify-center hover:bg-white/10"
                style={{ border: `1px solid ${T.border}`, color: T.textMuted }}
                aria-label="Acorde siguiente"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </>
        )}

        {phase === "complete" && (
          <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div
              className="rounded-2xl px-8 py-6 text-center module-complete-pop"
              style={{
                background: "rgba(28,26,25,0.94)",
                border: `1px solid ${T.accent}`,
                boxShadow: `0 0 40px ${T.accentGlow}`,
              }}
            >
              <p className="text-lg font-bold" style={{ color: T.textMain }}>
                ¡Drill completado!
              </p>
              <p className="text-xs mt-1" style={{ color: T.textDim }}>
                Volviendo al hub…
              </p>
            </div>
            <style>{`
              .module-complete-pop { animation: moduleCompletePop 0.5s ease-out both; }
              @keyframes moduleCompletePop {
                from { opacity: 0; transform: scale(0.92); }
                to { opacity: 1; transform: scale(1); }
              }
            `}</style>
          </div>
        )}
      </div>
    </ModuleStudioShell>
  );
}
